import type { Lead, LeadContactType } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem } from '@heroui/react';
import { toast } from 'react-toastify';

interface AddContactModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  isSubmitting: boolean;
  onSubmit: (type: LeadContactType, value: string, source?: string) => void;
}

const CONTACT_TYPES: Array<{ key: LeadContactType; label: string }> = [
  { key: 'email', label: i18n.t('auth.email') },
  { key: 'phone', label: i18n.t('copy.phone') },
  { key: 'linkedin', label: i18n.t('copy.linkedin') },
  { key: 'facebook', label: i18n.t('copy.facebook') },
  { key: 'twitter', label: i18n.t('copy.twitter') },
  { key: 'instagram', label: i18n.t('copy.instagram') }
];

export function AddContactModal({ isOpen, onOpenChange, lead, isSubmitting, onSubmit }: AddContactModalProps) {
  const [type, setType] = React.useState<LeadContactType>('email');
  const [value, setValue] = React.useState('');
  const [source, setSource] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setType('email');
      setValue('');
      setSource('');
    }
  }, [isOpen]);

  const handleSubmit = React.useCallback(() => {
    if (!value.trim()) {
      toast.error(i18n.t('copy.please_enter_a_contact_value'));
      return;
    }

    onSubmit(type, value.trim(), source.trim() || undefined);
  }, [type, value, source, onSubmit]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!lead) return null;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Add Contact for {lead.domain}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Select
                  label={i18n.t('copy.contact_type')}
                  placeholder={i18n.t('copy.select_contact_type')}
                  selectedKeys={new Set([type])}
                  onSelectionChange={(keys) => {
                    const selectedType = Array.from(keys)[0] as LeadContactType;
                    if (selectedType) setType(selectedType);
                  }}
                  isRequired
                >
                  {CONTACT_TYPES.map((contactType) => (
                    <SelectItem key={contactType.key}>{contactType.label}</SelectItem>
                  ))}
                </Select>

                <Input
                  label={i18n.t('copy.contact_value')}
                  placeholder={
                    type === 'email'
                      ? 'email@example.com'
                      : type === 'phone'
                        ? '+1234567890'
                        : 'https://social-network.com/username'
                  }
                  value={value}
                  onValueChange={setValue}
                  isRequired
                  autoFocus
                />

                <Input
                  label={i18n.t('copy.source_optional')}
                  placeholder={i18n.t('copy.e_g_website_linkedin_manual')}
                  value={source}
                  onValueChange={setSource}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose} isDisabled={isSubmitting}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
                Add Contact
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default AddContactModal;

